import { PUB_SUB_EVENTS } from "./gw.js";
import { publish, subscribe } from "./pubsub.js";
import { fetchConfig } from "./shopify.js";

const SOURCE = "gift-with-purchase";
const GIFT_PROPERTY = "_gwp";
const GIFT_PROPERTY_VALUE = "true";
const config = window.giftWithPurchaseConfig;

if (config) {
  let reconciliation = Promise.resolve();
  let queued = false;

  const getSectionsToRender = () => {
    const sections = [];

    if (document.getElementById("CartDrawer")) {
      sections.push("cart-drawer");
    }
    if (document.getElementById("cart-icon-bubble")) {
      sections.push("cart-icon-bubble");
    }
    if (document.getElementById("main-cart-items")) {
      sections.push("main-cart-items");
    }
    if (document.getElementById("main-cart-footer")) {
      sections.push("main-cart-footer");
    }

    return sections;
  };

  const getCart = async () => {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart: ${response.status}`);
    }

    return response.json();
  };

  const isGiftLine = (item) =>
    item.properties && item.properties[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE;

  const getThreshold = (currency) => {
    if (currency === "USD") {
      return config.thresholdUsdCents;
    }

    const currencyRate = Number(window.Shopify?.currency?.rate);
    if (!Number.isFinite(currencyRate) || currencyRate <= 0) {
      console.warn(
        `[${SOURCE}] The USD threshold could not be converted to ${currency}.`,
      );
      return null;
    }

    return Math.round(config.thresholdUsdCents * currencyRate);
  };

  const isRegionEligible = () =>
    config.region === "all" ||
    (config.region === "us" && config.countryCode === "US");

  const shouldHaveGift = (cart, giftLines) => {
    if (
      !config.enabled ||
      !config.available ||
      !config.variantId ||
      config.thresholdUsdCents <= 0 ||
      !isRegionEligible()
    ) {
      return false;
    }

    const threshold = getThreshold(cart.currency);
    if (threshold === null) return false;

    const giftTotal = giftLines.reduce(
      (total, item) => total + item.final_line_price,
      0,
    );

    return cart.total_price - giftTotal >= threshold;
  };

  const updateRenderedSections = (cart) => {
    if (!cart.sections) return;

    const replacements = [
      ["cart-drawer", "#CartDrawer"],
      ["cart-icon-bubble", ".shopify-section"],
      ["main-cart-items", ".js-contents"],
      ["main-cart-footer", ".js-contents"],
    ];

    replacements.forEach(([sectionId, selector]) => {
      const sectionHtml = cart.sections[sectionId];
      if (!sectionHtml) return;

      const source = new DOMParser()
        .parseFromString(sectionHtml, "text/html")
        .querySelector(selector);
      let target;

      if (sectionId === "cart-drawer") {
        target = document.querySelector("#CartDrawer");
      } else if (sectionId === "cart-icon-bubble") {
        target = document.getElementById("cart-icon-bubble");
      } else {
        target = document.getElementById(sectionId)?.querySelector(selector);
      }

      if (source && target) {
        target.innerHTML = source.innerHTML;

        if (sectionId === "cart-drawer") {
          const drawer = document.querySelector("cart-drawer");
          const overlay = drawer?.querySelector("#CartDrawer-Overlay");
          if (drawer && overlay) {
            overlay.addEventListener("click", drawer.close.bind(drawer));
          }
        }
      }
    });
  };

  const mutateCart = async (url, body) => {
    const sections = getSectionsToRender();
    const response = await fetch(url, {
      ...fetchConfig(),
      body: JSON.stringify({
        ...body,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();

    if (!response.ok || cart.status) {
      throw new Error(
        cart.description || `Unable to update cart: ${response.status}`,
      );
    }

    updateRenderedSections(cart);
    await publish(PUB_SUB_EVENTS.cartUpdate, {
      source: SOURCE,
      cartData: cart,
    });

    return cart;
  };

  const reconcile = async () => {
    const cart = await getCart();
    const giftLines = cart.items.filter(isGiftLine);
    const eligible = shouldHaveGift(cart, giftLines);
    const currentGift = giftLines.find(
      (item) => item.variant_id === Number(config.variantId),
    );
    const staleGiftLines = giftLines.filter(
      (item) => item.key !== currentGift?.key,
    );

    if (
      eligible &&
      currentGift &&
      currentGift.quantity === 1 &&
      !staleGiftLines.length
    ) {
      return;
    }

    if (eligible && !currentGift) {
      const updates = Object.fromEntries(
        giftLines.map((item) => [item.key, 0]),
      );

      if (giftLines.length) {
        await mutateCart(routes.cart_update_url, { updates });
      }

      await mutateCart(routes.cart_add_url, {
        items: [
          {
            id: Number(config.variantId),
            quantity: 1,
            properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE },
          },
        ],
      });
      return;
    }

    if (!giftLines.length) return;

    const updates = Object.fromEntries(
      giftLines.map((item) => [
        item.key,
        eligible && item.key === currentGift?.key ? 1 : 0,
      ]),
    );
    await mutateCart(routes.cart_update_url, { updates });
  };

  const scheduleReconciliation = () => {
    if (queued) return reconciliation;
    queued = true;

    reconciliation = reconciliation
      .catch(() => undefined)
      .then(async () => {
        queued = false;
        await reconcile();
      })
      .catch((error) => {
        console.error(`[${SOURCE}]`, error);
      });

    return reconciliation;
  };

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === SOURCE) return;
    return scheduleReconciliation();
  });

  scheduleReconciliation();
}
